import { WebGLTextureSource } from "../../types/webgl_types.js";

type Placement<ImageId> = {
  id: ImageId;

  // контент без паддинга
  x: number;
  y: number;
  width: number;
  height: number;

  // прямоугольник с паддингом
  padding_x: number;
  padding_y: number;
  padding_width: number;
  padding_height: number;

  image: WebGLTextureSource;
};

type WebGLSpriteMeta = {
  // в пикселях, без поддинга
  x: number;
  y: number;
  w: number;
  h: number;

  // UV контента (без паддинга)
  u0: number;
  v0: number;
  u1: number;
  v1: number;
};

export type WebGLAtlasData<ImageId> = {
  image: WebGLTextureSource;
  width: number;
  height: number;
  padding: number;
  sprites: Map<ImageId, WebGLSpriteMeta>;
  getUVRect( id: ImageId ): IUVBounds;
};

export type WebGLAtlas<ImageId> = {
  data: WebGLAtlasData<ImageId>;
  texture: WebGLTexture;
  getUVRect( id: ImageId ): IUVBounds;
  destroy( gl: WebGL2RenderingContext ): void;
};

export type WebGLSpriteInput<ImageId> = { id: ImageId; image: WebGLTextureSource };

export type WebGLAtlasOptions = {
  max_width?: number;  // макс. ширина атласа в пикселях (дефолт 1024)
  max_height?: number; // макс. высота атласа в пикселях (дефолт max_width)
  padding?: number;  // паддинг с экструзией в пикселях (дефолт 2)
  power_of_two?: boolean; // округлить размеры атласа до степеней двойки (дефолт false)
  min_filter?: number;  // дефолт gl.LINEAR (без мипов)
  mag_filter?: number;  // дефолт gl.LINEAR
  wrap_s?: number;  // дефолт gl.CLAMP_TO_EDGE
  wrap_t?: number;  // дефолт gl.CLAMP_TO_EDGE
  generate_mipmaps?: boolean; // сгенерировать мипы (WebGL2 разрешает NPOT) — дефолт false
};

export interface IUVBounds
{
  min_x: number;
  min_y: number;
  max_x: number;
  max_y: number;
}

export class WebGLStaticAtlasBuilder
{
  private static nextPow2( n: number ): number
  {
    if ( n <= 0 ) return 1;
    let p = 1;
    while ( p < n ) p <<= 1;
    return p;
  }

  /** Нарисовать картинку с экструзией краев в паддинг */
  private static drawWithExtrude(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    img: WebGLTextureSource,
    x: number,
    y: number,
    padding: number,
  ): void
  {
    const width = img.width, height = img.height;
    // центр
    ctx.drawImage( img, x, y );

    if ( padding <= 0 ) return;
    ctx.imageSmoothingEnabled = false;

    // рёбра (масштабируем полоску 1px)
    ctx.drawImage( img, 0, 0, 1, height, x - padding, y, padding, height );       // left
    ctx.drawImage( img, width - 1, 0, 1, height, x + width, y, padding, height ); // right
    ctx.drawImage( img, 0, 0, width, 1, x, y - padding, width, padding );         // top
    ctx.drawImage( img, 0, height - 1, width, 1, x, y + height, width, padding ); // bottom

    // углы (масштабируем пиксель 1×1)
    ctx.drawImage( img, 0, 0, 1, 1, x - padding, y - padding, padding, padding );               // TL
    ctx.drawImage( img, width - 1, 0, 1, 1, x + width, y - padding, padding, padding );         // TR
    ctx.drawImage( img, 0, height - 1, 1, 1, x - padding, y + height, padding, padding );       // BL
    ctx.drawImage( img, width - 1, height - 1, 1, 1, x + width, y + height, padding, padding ); // BR
  }

  /** Простой shelf-пакер: сортирует по высоте и раскладывает в строки до max_width */
  private static packSprites<ImageId>(
    inputs: WebGLSpriteInput<ImageId>[],
    max_width: number,
    padding: number,
    max_height?: number,
  ): { placements: Placement<ImageId>[]; width: number; height: number }
  {
    const list = [...inputs].sort( ( a, b ) => b.image.height - a.image.height );

    const placements: Placement<ImageId>[] = [];
    let x = 0;
    let y = 0;
    let row_height = 0;
    let used_width = 0;

    for ( const it of list )
    {
      const width = it.image.width;
      const height = it.image.height;
      const tex_row_width = width + 2 * padding;
      const tex_row_height = height + 2 * padding;

      if ( tex_row_width > max_width )
        throw new Error( `Sprite ${it.id} is too wide (${tex_row_width}px with padding) for max_width ${max_width}px` );

      if ( max_height !== undefined && tex_row_height > max_height )
        throw new Error( `Sprite ${it.id} is too tall (${tex_row_height}px with padding) for max_height ${max_height}px` );

      if ( x > 0 && x + tex_row_width > max_width )
      { // новый ряд
        y += row_height;
        x = 0;
        row_height = 0;
      }
      placements.push( {
        id: it.id, x: x + padding, y: y + padding, width: width, height: height,
        padding_x: x, padding_y: y, padding_width: tex_row_width, padding_height: tex_row_height, image: it.image
      } );
      x += tex_row_width;
      row_height = Math.max( row_height, tex_row_height );
      used_width = Math.max( used_width, x );
    }
    const used_height = y + row_height;
    return { placements, width: used_width, height: used_height };
  }

  /** Главная функция: строит атлас, возвращает текстуру и UV-метаданные */
  public static build<ImageId>(
    gl: WebGL2RenderingContext,
    sprites: WebGLSpriteInput<ImageId>[],
    options: WebGLAtlasOptions = {},
  ): WebGLAtlas<ImageId>
  {
    const data = this.buildAtlasData( sprites, options );
    return this.createAtlasFromData( gl, data, options );
  }

  /** Создает только данные атласа (картинку и UV), без привязки к WebGL контексту */
  public static buildAtlasData<ImageId>(
    sprites: WebGLSpriteInput<ImageId>[],
    options: WebGLAtlasOptions = {},
  ): WebGLAtlasData<ImageId>
  {
    const padding = options.padding ?? 2;
    const max_width = options.max_width ?? 1024;
    const max_height = options.max_height ?? max_width;

    // 1) Упаковываем
    const packed = this.packSprites( sprites, max_width, padding, max_height );
    let atlas_width = packed.width;
    let atlas_height = packed.height;

    if ( options.power_of_two )
    {
      atlas_width = this.nextPow2( atlas_width );
      atlas_height = this.nextPow2( atlas_height );
    }

    if ( max_width !== undefined && atlas_width > max_width )
    {
      throw new Error(
        `[WebGLStaticAtlasBuilder] Atlas overflow: compiled width (${atlas_width}px, raw: ${packed.width}px) exceeds max_width (${max_width}px). Increase atlas size limit.`
      );
    }

    if ( max_height !== undefined && atlas_height > max_height )
    {
      throw new Error(
        `[WebGLStaticAtlasBuilder] Atlas overflow: compiled height (${atlas_height}px, raw: ${packed.height}px) exceeds max_height (${max_height}px). Increase atlas size limit.`
      );
    }

    // 2) Рисуем на конвасе
    const canvas = this.createCanvas( atlas_width, atlas_height );
    const ctx = canvas.getContext( "2d", { alpha: true } ) as ( CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null );
    if ( !ctx )
      throw new Error( "Failed to get the 2D context" );

    ctx.clearRect( 0, 0, atlas_width, atlas_height );
    for ( const p of packed.placements )
      this.drawWithExtrude( ctx, p.image, p.x, p.y, padding );

    // 3) Собираем UV-метаданные (для контента – без паддинга)
    const meta = new Map<ImageId, WebGLSpriteMeta>();
    for ( const sprite of packed.placements )
    {
      const u0 = sprite.x / atlas_width;
      const v0 = sprite.y / atlas_height;                       // Верх спрайта (меньший V)
      const u1 = ( sprite.x + sprite.width ) / atlas_width;
      const v1 = ( sprite.y + sprite.height ) / atlas_height;   // Низ спрайта (больший V)
      
      meta.set( sprite.id, { x: sprite.x, y: sprite.y, w: sprite.width, h: sprite.height, u0, v0, u1, v1 } );
    }

    return {
      image: canvas,
      width: atlas_width,
      height: atlas_height,
      padding: padding,
      sprites: meta,
      getUVRect( id: ImageId )
      {
        const sprite = meta.get( id );
        if ( !sprite )
          throw new Error( `Sprite "${id}" not found in atlas` );
        return {
          min_x: sprite.u0,
          min_y: sprite.v0,
          max_x: sprite.u1,
          max_y: sprite.v1,
        };
      }
    };
  }

  private static shared_atlases = new WeakMap<WebGL2RenderingContext, Map<WebGLAtlasData<any>, { atlas: WebGLAtlas<any>; refs: number }>>();

  /** Создает WebGL текстуру из готовых данных атласа */
  public static createAtlasFromData<ImageId>(
    gl: WebGL2RenderingContext,
    data: WebGLAtlasData<ImageId>,
    options: WebGLAtlasOptions = {},
  ): WebGLAtlas<ImageId>
  {
    if ( Object.keys( options ).length > 0 )
      return this.uploadAtlas( gl, data, options );

    let per_context = this.shared_atlases.get( gl );
    if ( !per_context )
    {
      per_context = new Map();
      this.shared_atlases.set( gl, per_context );
    }

    let entry = per_context.get( data );
    if ( !entry )
    {
      const atlas = this.uploadAtlas( gl, data, options );
      const shared = per_context;
      const owner = { atlas, refs: 0 };
      owner.atlas = {
        ...atlas,
        destroy( context: WebGL2RenderingContext )
        {
          owner.refs--;
          if ( owner.refs > 0 )
            return;
          shared.delete( data );
          atlas.destroy( context );
        },
      };
      entry = owner;
      per_context.set( data, entry );
    }

    entry.refs++;
    return entry.atlas;
  }

  private static uploadAtlas<ImageId>(
    gl: WebGL2RenderingContext,
    data: WebGLAtlasData<ImageId>,
    options: WebGLAtlasOptions,
  ): WebGLAtlas<ImageId>
  {
    const texture = gl.createTexture();
    if ( !texture )
      throw new Error( "Failed to create the webgl texture" );

    gl.bindTexture( gl.TEXTURE_2D, texture );
    gl.pixelStorei( gl.UNPACK_FLIP_Y_WEBGL, false );
    gl.pixelStorei( gl.UNPACK_ALIGNMENT, 1 ); 

    gl.texImage2D( gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, data.image as TexImageSource );

    const default_min_filter = options.generate_mipmaps ? gl.LINEAR_MIPMAP_LINEAR : gl.LINEAR;
    const wrap_s = options.wrap_s ?? gl.CLAMP_TO_EDGE;
    const wrap_t = options.wrap_t ?? gl.CLAMP_TO_EDGE;
    const min_filter = options.min_filter ?? default_min_filter;
    const mag_filter = options.mag_filter ?? gl.LINEAR;

    gl.texParameteri( gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, wrap_s );
    gl.texParameteri( gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, wrap_t );
    gl.texParameteri( gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, min_filter );
    gl.texParameteri( gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, mag_filter );

    if ( options.generate_mipmaps )
      gl.generateMipmap( gl.TEXTURE_2D );

    return {
      data,
      texture,
      getUVRect( id: ImageId ): IUVBounds
      {
        return data.getUVRect( id );
      },
      destroy( gl: WebGL2RenderingContext )
      {
        gl.deleteTexture( texture );
      }
    };
  }

  private static createCanvas( width: number, height: number ): OffscreenCanvas | HTMLCanvasElement
  {
    if ( typeof OffscreenCanvas !== "undefined" )
      return new OffscreenCanvas( width, height );

    const canvas = document.createElement( "canvas" );
    canvas.width = width;
    canvas.height = height;
    return canvas;
  }
}
